import { DEFAULT_STUDY_TIMEZONE, SHORT_MONTHS, todayInTimezone } from './studyTermProgress';

/**
 * The one date format for learner-visible Study dates (study-capture-fixes-prd.md F-08):
 * "Sat 11 Oct" in the current year, "Mon 2 Feb 2027" in another year. A learner never sees an
 * ISO date such as 2026-10-11.
 */

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Three-letter weekday names, Sunday first (`Date.getUTCDay`). */
const SHORT_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export interface StudyDateOptions {
  /** The learner's academic timezone; it decides "today" and the day of a timestamp. */
  timezone?: string | null;
  /** Fixed "now" for tests. */
  now?: Date;
}

/** The calendar day (YYYY-MM-DD) of a date or timestamp in the academic timezone. */
export function studyCalendarDay(value: string, timezone?: string | null): string | null {
  if (CALENDAR_DATE.test(value)) return value;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return todayInTimezone(timezone ?? DEFAULT_STUDY_TIMEZONE, instant);
}

/**
 * "Sat 11 Oct", or "Mon 2 Feb 2027" when the date is not in the current year of the academic
 * timezone. A YYYY-MM-DD value is a calendar date and never moves with the device timezone; a
 * timestamp is read as a day in the academic timezone. An unreadable value returns ''.
 */
export function formatStudyDate(value: string, options: StudyDateOptions = {}): string {
  const day = studyCalendarDay(value, options.timezone);
  const match = day ? CALENDAR_DATE.exec(day) : null;
  if (!match) return '';
  const [, year, month, date] = match;
  // UTC keeps the calendar date exact on every device. Fixed names, not `Intl`: ICU builds differ
  // on "Sep" / "Sept" and on a comma after the weekday ("Sat, 11 Oct").
  const utc = new Date(Date.UTC(Number(year), Number(month) - 1, Number(date)));
  const weekday = SHORT_WEEKDAYS[utc.getUTCDay()];
  const label = `${weekday} ${utc.getUTCDate()} ${SHORT_MONTHS[utc.getUTCMonth()]}`;
  const today = todayInTimezone(options.timezone ?? DEFAULT_STUDY_TIMEZONE, options.now);
  return today.slice(0, 4) === year ? label : `${label} ${year}`;
}

/** "Due Sat 11 Oct" for a task or assessment date. */
export function formatStudyDueDate(value: string, options: StudyDateOptions = {}): string {
  const label = formatStudyDate(value, options);
  return label ? `Due ${label}` : '';
}
